import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  TextField,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MyButton } from '../index';
import { rejectReasonModalStyles } from './RejectReasonModal.styles';

const RejectReasonModal = ({
  open = false,
  onClose,
  onConfirm,
  title,
  message,
  itemName = '',
  loading = false,
  entityType = 'leave',
  ...props
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [reason, setReason] = useState('');

  const handleConfirm = async () => {
    if (onConfirm && reason.trim()) {
      await onConfirm(reason.trim());
      setReason('');
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      onClose();
    }
  };

  const modalTitle = title || t(`${entityType}.modals.reject.title`);
  const modalMessage = message || t(`${entityType}.modals.reject.message`);

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose(e, reason);
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      sx={rejectReasonModalStyles.dialog}
      {...props}
    >
      <DialogTitle sx={rejectReasonModalStyles.dialogTitle}>
        <Box sx={rejectReasonModalStyles.titleContainer}>
          <Box sx={rejectReasonModalStyles.titleContent}>
            <CancelIcon sx={rejectReasonModalStyles.cancelIcon} />
            <Typography variant="h6" sx={rejectReasonModalStyles.titleText}>
              {modalTitle}
            </Typography>
          </Box>

          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={rejectReasonModalStyles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={rejectReasonModalStyles.dialogContent}>
        <Typography variant="body1" sx={rejectReasonModalStyles.messageText}>
          {modalMessage}
        </Typography>
        {itemName && (
          <Box sx={rejectReasonModalStyles.itemNameContainer}>
            <Typography variant="body1" sx={rejectReasonModalStyles.itemName}>
              "{itemName}"
            </Typography>
          </Box>
        )}
        <TextField
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          label={t(`${entityType}.modals.reject.reasonLabel`)}
          placeholder={t(`${entityType}.modals.reject.reasonPlaceholder`)}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={rejectReasonModalStyles.textField}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions sx={rejectReasonModalStyles.dialogActions}>
        <MyButton
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={rejectReasonModalStyles.cancelButton}
        >
          {t('modals.delete.cancel')}
        </MyButton>

        <MyButton
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={loading || !reason.trim()}
          sx={rejectReasonModalStyles.confirmButton}
        >
          {loading ? t(`${entityType}.modals.reject.rejecting`) : t(`${entityType}.modals.reject.confirm`)}
        </MyButton>
      </DialogActions>
    </Dialog>
  );
};

export default RejectReasonModal; 